// KanbanCard — single draggable wish tile in the Wishes.tsx kanban.
//
// Per `docs/spec/wishlist/DESIGN.md §5.4`:
//   - paper-deep bg + hairline border (no shadow)
//   - padding s-5 (20px) per spec
//   - 4px priority strip on the LEFT (paper-deep / paper-calm / hairline)
//   - title h3 16/600 with 2-line truncate
//   - 6px progress bar (hairline track + ink-1 fill) only when progress/budget block exists
//   - footer countdown: 還有 N 天 / 過期 N 天 / 完成於 M/D
//
// Per `docs/spec/wishlist/PORT_PLAN.md §3.2`:
//   - `bg-paper-deep` (not v2 `bg-surface` white)
//   - hairline border (not v2 shadow-1)
//   - 1 progress bar tier only (not v2 progress-1 / progress-2)
//
// Drag-and-drop: each card is a `@dnd-kit/core` `useDraggable`. The
// `attributes` + `listeners` + `setNodeRef` are spread onto the card root.
//
// `lucide-react` icon monoline 1.5 stroke per `DESIGN.md §14` + AGENTS.md
// §3 #19 (no emoji as UI icon).

import { forwardRef, useMemo } from "react";
import { useDraggable } from "@dnd-kit/core";
import { CalendarDays, CheckCircle2, Clock } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import type {
  WishPriority,
  WishStatus,
  WishSummary,
} from "@/lib/api/wishlist";

const PRIORITY_STRIP: Record<WishPriority, string> = {
  // per DESIGN §4.3 — no v2 conflict/shared strip; use paper tone deltas
  must: "bg-paper-deep", // 4px strip = paper-deep (#f8f4eb) — the most "obvious深"
  want: "bg-paper-calm", // paper-calm (#f3efe6)
  maybe: "bg-hairline", // hairline (rgb 28 28 30 / 0.06) — almost invisible
};

const PRIORITY_TEXT: Record<WishPriority, string> = {
  must: "必做",
  want: "想要",
  maybe: "也許",
};

export interface KanbanCardProps {
  wish: WishSummary;
  /** Today date (ISO YYYY-MM-DD) for footer countdown rendering. */
  today: string;
  /** When false, render the card as read-only (no drag). */
  draggable?: boolean;
  /** Optional click handler (W33.2+ mobile → BottomSheet). */
  onClick?: () => void;
  className?: string;
}

function dayDelta(targetDate: string | null, today: string): number | null {
  if (!targetDate) return null;
  // ISO YYYY-MM-DD string compare is safe; use ms arithmetic.
  const t = Date.parse(targetDate + "T00:00:00Z");
  const n = Date.parse(today + "T00:00:00Z");
  if (Number.isNaN(t) || Number.isNaN(n)) return null;
  return Math.round((t - n) / 86_400_000);
}

function footerLabel(
  status: WishStatus,
  targetDate: string | null,
  today: string,
): { text: string; tone: "future" | "past" | "done" | "none" } {
  if (status === "completed") {
    return { text: "已完成", tone: "done" };
  }
  const delta = dayDelta(targetDate, today);
  if (delta === null) return { text: "未設日期", tone: "none" };
  if (delta === 0) return { text: "今天", tone: "future" };
  if (delta > 0) return { text: `還有 ${delta} 天`, tone: "future" };
  return { text: `過期 ${Math.abs(delta)} 天`, tone: "past" };
}

export const KanbanCard = forwardRef<HTMLDivElement, KanbanCardProps>(
  function KanbanCard(
    { wish, today, draggable = true, onClick, className },
    ref,
  ) {
    const footer = useMemo(
      () => footerLabel(wish.status, wish.target_date, today),
      [wish.status, wish.target_date, today],
    );
    const showProgress =
      wish.progress_percent > 0 &&
      wish.progress_percent <= 100 &&
      wish.status !== "completed";

    // @dnd-kit/core — wire the card to the DndContext in Wishes.tsx.
    // The route listens to onDragEnd at the context level; here we just
    // expose `attributes` (data-* + aria) and `listeners` (pointer events)
    // for the card root.
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      isDragging,
    } = useDraggable({
      id: wish.id,
      disabled: !draggable,
    });

    return (
      <article
        ref={(el) => {
          // React's `ref` callback is typed as `(node: HTMLElement | null) => void`;
          // our `forwardRef<HTMLDivElement>` is narrower than that, so
          // coerce to `HTMLDivElement | null` at the boundary. The dnd-kit
          // `setNodeRef` accepts the same `HTMLElement | null` shape and is
          // happy with the wider type.
          const div = el as HTMLDivElement | null;
          setNodeRef(div);
          if (typeof ref === "function") ref(div);
          else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = div;
          }
        }}
        data-component="KanbanCard"
        data-testid={`kanban-card-${wish.id}`}
        data-priority={wish.priority}
        data-status={wish.status}
        onClick={onClick}
        className={cn(
          // base: paper-deep bg + hairline border + s-5 padding (DESIGN §5.4)
          "bg-paper-deep border border-hairline rounded-2xl p-s-5",
          // 4px left priority strip
          "relative overflow-hidden",
          isDragging
            ? "opacity-40 cursor-grabbing z-50 shadow-modal"
            : draggable
              ? "cursor-grab hover:border-ink-3"
              : onClick
                ? // Not draggable but still tappable (mobile path). Must NOT
                  // look inert.
                  "cursor-pointer"
                : "cursor-default",
          "press",
          className,
        )}
        style={
          transform
            ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
            : undefined
        }
        {...attributes}
        {...listeners}
        // dnd-kit sets `aria-disabled="true"` whenever `draggable` is false,
        // which on the mobile path is true for a card that is fully tappable.
        // Overriding it after the spread keeps the accessible state honest:
        // a button that opens a BottomSheet is not disabled.
        aria-disabled={draggable ? attributes["aria-disabled"] : undefined}
      >
        <span
          data-testid="kanban-card-priority-strip"
          className={cn(
            "absolute left-0 top-0 bottom-0 w-1",
            PRIORITY_STRIP[wish.priority],
          )}
          aria-hidden
        />
        <header className="flex items-start gap-2 mb-2">
          <h3
            data-testid="kanban-card-title"
            className="font-display text-body font-semibold text-ink-1 leading-snug line-clamp-2 flex-1 min-w-0"
          >
            {wish.name}
          </h3>
          <span
            data-testid="kanban-card-priority-label"
            className="shrink-0 text-meta text-ink-3"
            title={`優先度: ${PRIORITY_TEXT[wish.priority]}`}
          >
            {PRIORITY_TEXT[wish.priority]}
          </span>
        </header>
        {showProgress ? (
          <div
            data-testid="kanban-card-progress"
            className="mb-2"
            aria-label={`進度 ${wish.progress_percent}%`}
          >
            <div className="h-1.5 rounded-full bg-hairline overflow-hidden">
              <div
                data-testid="kanban-card-progress-bar"
                className="h-full bg-ink-1"
                style={{ width: `${wish.progress_percent}%` }}
              />
            </div>
            <p className="mt-1 text-meta text-ink-3 tabular-nums">
              {wish.progress_percent}%
            </p>
          </div>
        ) : null}
        <footer className="flex items-center gap-1 text-meta text-ink-2">
          {footer.tone === "done" ? (
            <CheckCircle2 className="h-3 w-3 text-shared-strong" aria-hidden />
          ) : footer.tone === "future" ? (
            <CalendarDays className="h-3 w-3" aria-hidden />
          ) : footer.tone === "past" ? (
            <Clock className="h-3 w-3" aria-hidden />
          ) : null}
          <span
            data-testid="kanban-card-footer-text"
            className={cn(
              footer.tone === "past" && "text-ink-1",
              footer.tone === "done" && "text-shared-strong",
            )}
          >
            {footer.text}
          </span>
        </footer>
      </article>
    );
  },
);
